import { useMutation } from '@tanstack/react-query'
import { http } from '../../utils/httpCommon'
import { apiUrls } from '../../utils/apiUrls'
import { z } from 'zod'
import {
  isDispatchMobileProject,
  isDockAutomationProject,
  SENTRY_PROJECT_APEX_CLIENT,
  SENTRY_PROJECT_DISPATCH_MOBILE,
  SENTRY_PROJECT_DOCK_AUTOMATION,
} from '../../constants/sentryProjects'
import { getWorkstreamCulpritQuery } from '../../utils/workstreamHelper'
import { formatApiDateStart, formatApiDateEnd } from '../../utils/dateUtils'

// Zod schema for export API response
const exportIssueSchema = z.object({
  Issue: z.string(),
  'Issue ID': z.string(),
  'Short ID': z.string(),
  Environment: z.string(),
  // Last Seen can be a Date object or string - coerce to string
  'Last Seen': z.coerce.string(),
  Age: z.string(),
  Events: z.number(),
  Users: z.number(),
  Priority: z.string(),
})

const exportIssuesApiResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.array(exportIssueSchema),
  recordCount: z.number(),
})

export type ExportIssue = z.infer<typeof exportIssueSchema>
export type ExportIssuesResponse = z.infer<typeof exportIssuesApiResponseSchema>

interface ExportIssuesParams {
  environment?: string
  startDate?: Date | null
  endDate?: Date | null
  workstream?: string | null
  searchQuery?: string
  /** Sentry project ID (e.g. Dispatch Mobile: 4508858067189760) */
  projectId?: string
}

export const useExportIssues = () => {
  return useMutation({
    mutationFn: async (params: ExportIssuesParams) => {
      const queryParams = new URLSearchParams()

      // Add required expand parameters (must be first)
      queryParams.append('expand', 'owners')
      queryParams.append('expand', 'inbox')

      // Check if date range is 24 hours
      const is24Hours =
        params.startDate &&
        params.endDate &&
        Math.abs(params.endDate.getTime() - params.startDate.getTime()) <= 24 * 60 * 60 * 1000

      // Add statsPeriod if 24 hours, otherwise use start/end dates
      if (is24Hours) {
        queryParams.append('statsPeriod', '24h')
      } else if (params.startDate && params.endDate) {
        // Format dates using utility functions
        queryParams.append('start', formatApiDateStart(params.startDate))
        queryParams.append('end', formatApiDateEnd(params.endDate))
      }

      const projectId =
        params.projectId ??
        (params.workstream === 'dispatch-mobile'
          ? SENTRY_PROJECT_DISPATCH_MOBILE
          : params.workstream === 'dock-automation'
            ? SENTRY_PROJECT_DOCK_AUTOMATION
            : SENTRY_PROJECT_APEX_CLIENT)
      queryParams.append('project', projectId)

      if (isDispatchMobileProject(projectId) || isDockAutomationProject(projectId)) {
        if (params.searchQuery && params.searchQuery.trim()) {
          queryParams.append('query', `title:"${params.searchQuery.trim()}"`)
        }
      } else {
        // Build query parts
        const workstreamQuery = getWorkstreamCulpritQuery(params.workstream)
        const searchQueryPart = params.searchQuery?.trim() ? `title:"${params.searchQuery.trim()}"` : null

        // Combine query parts
        const queryParts = [workstreamQuery, searchQueryPart].filter(Boolean)
        if (queryParts.length > 0) {
          queryParams.append('query', queryParts.join(' '))
        }
      }

      // Add environment parameter
      if (params.environment && params.environment !== 'all') {
        queryParams.append('environment', params.environment)
      } else {
        queryParams.append('includeEnvironment', 'true')
      }

      // Replace + with %20 in the query string for proper space encoding
      const apiEndpoint = `${apiUrls.getExportIssues}?${queryParams.toString().replace(/\+/g, '%20')}`
      const response = await http.get(apiEndpoint)

      // Validate response with Zod schema
      const validatedData = exportIssuesApiResponseSchema.parse(response.data)
      return validatedData
    },
  })
}
